import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConnectedProps, connect } from "react-redux";
import { Circle, MapContainer, Polygon, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ReducerState } from "../../types";
import { getOrganization } from "../../actions";
import L, { LatLngTuple } from "leaflet";
import { useWebServer } from "../../providers";
import { Spin } from "antd";
import "leaflet.fullscreen/dist/Control.FullScreen.css";
import "./index.css";
import MarkerManager from "./MarkerManager";
import MapExpand from "./MapExpand";
import MapDownloadControl from "./MapDownload";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { MarkerData } from "../../types/components/map";
import { TrapSite } from "../../types/api/models/trapSite";
import {
  useMaintenanceAlertsCounts,
  useDetectionAlertsCounts,
} from "../../hooks/useAppAlerts";
import SatelliteLayer from "./SatelliteLayer";
import TrapsiteEditorLayer from "./TrapsiteEditor";
import { TrapsiteEditorContext } from "./TrapsiteEditor/TrapsiteEditorContext";
import { useTrapsiteEditor } from "./TrapsiteEditor/useTrapsiteEditor";
import { EditorDevice } from "./TrapsiteEditor/types";
import { radiusOf } from "./TrapsiteEditor/trapProximity";
import {
  BOUNDARY_STYLE,
  OVERLAP_REGION_STYLE,
} from "./TrapsiteEditor/constants";
import { overlapRegions } from "./TrapsiteEditor/boundaries";
import { toLatLng } from "../../utils/geo";

const mapStateToProps = (state: ReducerState) => {
  const map_center = state.devices?.selected?.gps ||
    state.organizations?.map_center || { lat: 32.7361613, lon: -98.2829359 };
  return {
    uuid: state.organizations?.uuid || null,
    map_center: [map_center?.lat, map_center?.lon] as LatLngTuple,
  };
};

const connector = connect(mapStateToProps, {
  getOrganization,
});

type Props = ConnectedProps<typeof connector> & {
  /** When provided, only the marker for this trapsite is shown (mesh/trapsite routes). */
  trapSite?: Partial<TrapSite>;
  deviceId?: string;
  /**
   * Enables right-click create/edit of collection locations.
   *
   * Opt-in because the other two call sites are the wrong place for it: the
   * location-detail map renders device-level markers, so "right-click a pin to
   * edit the location" would point at the wrong entity, and the specimen map
   * is too narrow to dock a panel over.
   */
  editable?: boolean;
};

const MapViewController: React.FC<{
  zoom: number;
  markers: LatLngTuple[];
  /** Circles the opening view must contain, as centres and metre radii. */
  encloses?: Array<{ center: LatLngTuple; radius: number }>;
}> = ({ zoom, markers, encloses = [] }) => {
  const map = useMap();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    if (markers.length === 0 && encloses.length === 0) return;

    // A boundary has to be visible to mean anything: at the zoom a single
    // location opens at, a kilometre-wide circle sits entirely off-screen.
    // toBounds gives a circle's extent without needing it on the map first.
    const bounds =
      markers.length > 0 ? L.latLngBounds(markers) : L.latLngBounds([]);
    for (const circle of encloses) {
      bounds.extend(L.latLng(circle.center).toBounds(circle.radius * 2));
    }

    if (bounds.isValid() && (markers.length > 1 || encloses.length > 0)) {
      map.fitBounds(bounds, { padding: [10, 10] });
    } else if (markers.length > 0) {
      map.setView(markers[0], zoom);
    }
    initialized.current = true;
  }, [map, markers, zoom, encloses]);

  return null;
};

const MapComponent: React.FC<Props> = ({
  map_center,
  uuid,
  getOrganization,
  trapSite,
  deviceId,
  editable = false,
}) => {
  // The wrapper hosts the editor's panel and menu, and is what expands in
  // pseudo-fullscreen so they travel with the map.
  const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null);
  const { sendRequest, isLoading } = useWebServer();

  useEffect(() => {
    if (!isLoading && !uuid) {
      getOrganization(sendRequest);
    }
  }, [getOrganization, sendRequest, isLoading, uuid]);

  const { counts: maintenanceCounts, isLoading: isMaintenanceLoading } =
    useMaintenanceAlertsCounts();
  const { counts: detectionCounts, isLoading: isDetectionLoading } =
    useDetectionAlertsCounts();

  const { data: trapSitesData, isLoading: isTrapSiteLoading } =
    useListTrapSitesQuery();
  const { data: devices } = useListDevicesQuery();

  const isDataLoading =
    isTrapSiteLoading || isMaintenanceLoading || isDetectionLoading;

  const markersData = useMemo((): MarkerData[] => {
    if (isDataLoading || !devices?.items || !trapSitesData?.items) return [];

    const deviceAgg = devices.items.map((device) => {
      const { uuid, display_name, trap_site_id, gps } = device;

      return {
        uuid: uuid || "",
        deviceName: display_name || "N/A",
        maintainCount: maintenanceCounts[device?.uuid || ""] || 0,
        detectionCount: detectionCounts[device?.uuid || ""] || 0,
        trapSite: trap_site_id || "",
        gps,
      };
    });

    return trapSitesData.items.map((trapSite) => {
      const devicesInTrapSite = deviceAgg.filter(
        (device) => device.trapSite === trapSite._id,
      );
      const detectionCount = devicesInTrapSite.reduce(
        (sum, d) => sum + (d.detectionCount || 0),
        0,
      );
      const maintainCount = devicesInTrapSite.reduce(
        (sum, d) => sum + (d.maintainCount || 0),
        0,
      );
      const trapCount = devicesInTrapSite.length;

      return {
        id: trapSite._id,
        displayName: trapSite.display_name,
        position: [
          trapSite.gps?.lat || 0,
          trapSite.gps?.lon || 0,
        ] as LatLngTuple,
        warning: maintainCount > 0 || detectionCount > 0,
        detectionsCount: detectionCount,
        maintainCount: maintainCount,
        trapCount: trapCount,
        devicesInTrapSite: devicesInTrapSite,
        popupOpen: true,
      };
    });
  }, [
    devices,
    trapSitesData,
    maintenanceCounts,
    detectionCounts,
    isDataLoading,
  ]);

  // The device picker needs every device in the org, annotated with the site
  // it currently belongs to - a device has exactly one, so adding one that is
  // already placed is a move, and the confirm has to name where from.
  const editorDevices = useMemo((): EditorDevice[] => {
    if (!devices?.items) return [];
    const siteNames = new Map(
      (trapSitesData?.items || []).map((t) => [t._id, t.display_name]),
    );
    return devices.items.map((device) => ({
      uuid: device.uuid || "",
      displayName: device.display_name || device.uuid || "",
      trapSiteId: device.trap_site_id || null,
      trapSiteName: device.trap_site_id
        ? siteNames.get(device.trap_site_id) || null
        : null,
      // A device's own position, distinct from its site's - a trap can sit a
      // field away from the address the site is registered at.
      gps: device.gps ?? null,
    }));
  }, [devices, trapSitesData]);

  const devicesForSite = useCallback(
    (siteId: string) =>
      (devices?.items || [])
        .filter((d) => d.trap_site_id === siteId)
        .map((d) => d.uuid || ""),
    [devices],
  );

  // map_center arrives as a [lat, lon] tuple for Leaflet; the editor wants the
  // domain shape. Used as the starting point for a trap that has no recorded
  // position of its own.
  const fallbackGps = useMemo(
    () => ({ lat: map_center[0], lon: map_center[1] }),
    [map_center],
  );

  const editor = useTrapsiteEditor(editable, devicesForSite, fallbackGps);

  // If deviceId is provided, use it as the map center; (specimen/mesh page)
  // otherwise fall back to trapSite GPS (location detail page) or Redux map_center(home page).
  const effectiveCenter = useMemo((): LatLngTuple => {
    if (deviceId) {
      const device = devices?.items?.find((d) => d.uuid === deviceId);
      if (device?.gps?.lat && device?.gps?.lon) {
        return [device.gps.lat, device.gps.lon];
      }
    }

    if (trapSite?.gps?.lat && trapSite?.gps?.lon) {
      return [trapSite.gps.lat, trapSite.gps.lon];
    }
    return map_center;
  }, [trapSite, map_center, deviceId, devices]);

  // When a specific trapSite is provided (mesh/specimen routes), show device-level markers.
  const visibleMarkers = useMemo((): MarkerData[] => {
    // While either editor is open the editing layer draws every pin that is
    // relevant - the thing being edited and what it relates to - so the
    // ordinary markers step aside entirely. Leaving them would put the whole
    // organization's pins on a map that is meant to show one relationship.
    if (editor.draft || editor.deviceDraft) return [];

    const base = markersData;

    if (!trapSite) return base;

    const match = base.find((m) => m.id === trapSite._id);
    if (!match?.devicesInTrapSite?.length) {
      return match ? [match] : [];
    }

    // If a specific device is requested, narrow to just that device.
    const relevantDevices = deviceId
      ? match.devicesInTrapSite.filter((d) => d.uuid === deviceId)
      : match.devicesInTrapSite;

    // Split devices: those WITH GPS get individual markers,
    // those WITHOUT GPS are aggregated into one trapSite-level marker.
    const withGps = relevantDevices.filter((d) => d.gps?.lat && d.gps?.lon);
    const withoutGps = relevantDevices.filter(
      (d) => !d.gps?.lat || !d.gps?.lon,
    );

    const deviceMarkers: MarkerData[] = withGps.map((device) => ({
      deviceUuid: device.uuid,
      displayName: device.deviceName,
      position: [device.gps!.lat, device.gps!.lon] as LatLngTuple,
      warning:
        (device.maintainCount || 0) > 0 || (device.detectionCount || 0) > 0,
      detectionsCount: device.detectionCount || 0,
      maintainCount: device.maintainCount || 0,
      trapCount: 1,
      markerType: "device" as const,
      gps: device.gps,
      popupOpen: true,
    }));

    // Aggregate GPS-less devices into one marker at the trapSite position
    if (withoutGps.length > 0) {
      deviceMarkers.push({
        displayName: match.displayName,
        position: match.position,
        warning: withoutGps.some(
          (d) => (d.maintainCount || 0) > 0 || (d.detectionCount || 0) > 0,
        ),
        detectionsCount: withoutGps.reduce(
          (s, d) => s + (d.detectionCount || 0),
          0,
        ),
        maintainCount: withoutGps.reduce(
          (s, d) => s + (d.maintainCount || 0),
          0,
        ),
        trapCount: withoutGps.length,
        popupOpen: true,
      });
    }

    return deviceMarkers;
  }, [markersData, trapSite, deviceId, editor.draft, editor.deviceDraft]);

  /**
   * The coverage circles to draw behind the markers.
   *
   * Every location on the dashboard, and just the one on a location's own
   * page. Empty while editing - the editor draws its own from the draft, so
   * the circle follows the radius field as it is changed.
   *
   * Also empty on the specimen and mesh views: those are deliberately zoomed
   * to a single trap, where a coverage circle is off-screen and says nothing
   * about the specimen being looked at.
   */
  const positionedSites = useMemo(
    () =>
      (trapSitesData?.items ?? []).filter(
        (site) => site.gps?.lat != null && site.gps?.lon != null,
      ),
    [trapSitesData],
  );

  const boundaries = useMemo(() => {
    if (editor.draft || editor.deviceDraft || deviceId) return [];

    const sites = trapSite && trapSite.gps ? [trapSite] : positionedSites;
    return sites.map((site) => ({
      key: site._id ?? site.display_name ?? "",
      center: toLatLng(site.gps!),
      radius: radiusOf(site),
    }));
  }, [trapSite, positionedSites, deviceId, editor.draft, editor.deviceDraft]);

  /**
   * The ground more than one location claims.
   *
   * Worked out across every location rather than only the drawn ones, so a
   * single location's page still shows where its coverage runs into a
   * neighbour's.
   */
  const overlaps = useMemo(() => {
    if (editor.draft || editor.deviceDraft || deviceId) return [];

    return overlapRegions(
      positionedSites.map((site) => ({
        key: site._id ?? site.display_name ?? "",
        center: site.gps!,
        radiusM: radiusOf(site),
      })),
    );
  }, [positionedSites, deviceId, editor.draft, editor.deviceDraft]);

  const zoom = deviceId ? 20 : trapSite ? 18 : 13;

  return (
    // The provider sits OUTSIDE MapContainer so MarkerManager -> Marker can
    // read it for its own contextmenu, while the layer's children inside can
    // still call useMap().
    <TrapsiteEditorContext.Provider value={editor}>
      <div className="map-container-big" ref={setWrapper}>
        {uuid && !isLoading ? (
          <MapContainer
            worldCopyJump={true}
            center={effectiveCenter}
            minZoom={3}
            maxZoom={20}
            zoom={zoom}
            scrollWheelZoom={true}
            style={{
              width: "100%",
              height: "100%",
            }}
            zoomControl={false}
          >
            <MapViewController
              zoom={zoom}
              markers={visibleMarkers.map((c) => c.position)}
              encloses={boundaries}
            />
            <SatelliteLayer />
            {/* What this location claims to cover. Only on its own page,
                where the map is about one location - on the dashboard it
                would be a screenful of overlapping circles. Suppressed while
                an editor is open, which draws its own from the live draft. */}
            {boundaries.map((bounds) => (
              <Circle
                key={`bounds-${bounds.key}`}
                center={bounds.center}
                radius={bounds.radius}
                pathOptions={BOUNDARY_STYLE}
              />
            ))}
            {/* After the circles, so the shared patch reads on top of both
                outlines rather than under one of them. */}
            {overlaps.map((region) => (
              <Polygon
                key={`overlap-${region.key}`}
                positions={region.ring.map(toLatLng)}
                pathOptions={OVERLAP_REGION_STYLE}
              />
            ))}
            <MarkerManager
              markersData={visibleMarkers}
              isLoading={isDataLoading}
            />
            {/* Expands the wrapper, not the Leaflet container, so the editor
              panel expands with the map. */}
            <MapExpand fullscreenElement={editable ? wrapper : undefined} />
            <MapDownloadControl />
            {editable && (
              <TrapsiteEditorLayer
                trapSites={trapSitesData?.items || []}
                devices={editorDevices}
                container={wrapper}
                // The location detail map is already scoped to one location,
                // so creating another from it would be odd; it offers Move
                // Trap only. Home offers both.
                allowCreateTrapsite={!trapSite}
                // On a location's own map, placing a trap means placing it at
                // that location.
                scopedTrapSiteId={trapSite?._id ?? null}
              />
            )}
          </MapContainer>
        ) : (
          <div className="place-holder-map">
            <Spin />
          </div>
        )}
      </div>
    </TrapsiteEditorContext.Provider>
  );
};

export default connector(MapComponent);
